<template>
  <v-main class="pt-0">
    <v-container class="containerSize">
      <v-row class="text-white text-left">
        <v-col cols="12" class="mb-3 mt-1">
          <div class="text-h6 mb-2 d-flex align-center">
            <div class="grey-circle d-flex align-center justify-center mr-2">
              <v-icon color="#063562" :icon="mdiLink" />
            </div>
            <h1>{{ $t('mal_link_a_minors_acct') }}</h1>
          </div>
        </v-col>
        <v-spacer />
      </v-row>
      <MessageDialog
        :show-dialog="showMinorAccountNotFound"
        :has-button-two="false"
        :title="$t('mal_account_not_found')"
        :message="
          $t('mal_no_minor_accounts_were_found', {
            customerServicePhone: customerServicePhoneNumber,
          })
        "
        :button-one-label="$t('L_OK')"
        @buttonOneAction="showMinorAccountNotFound = false"
      />
      <MessageDialog
        :show-dialog="showMinorAccountsNotLinked"
        :has-button-two="false"
        :title="$t('mal_account_not_linked')"
        :button-one-label="$t('L_OK')"
        @buttonOneAction="showMinorAccountsNotLinked = false"
      >
        <template #message>
          <p>{{ $t('mal_no_minor_accounts_were_linked') }}</p>
          <p>{{ $t('mal_please_try_to_relink') }}</p>
        </template>
      </MessageDialog>
      <NavigationConfirmationDialog
        v-if="showNavigationConfirmationDialog"
        @confirm-navigation="navigateToRoute"
        @cancel-navigation="showNavigationConfirmationDialog = false"
      />

      <v-stepper v-model="minorAccountLinkProcess" :vertical="isMobileView" class="bottomSpacing">
        <v-stepper-header v-show="!isMobileView">
          <v-stepper-item :complete="minorAccountLinkProcess > 1" value="1">
            {{ $t('mal_step_enter_information_header') }}
          </v-stepper-item>
          <v-divider />
          <v-stepper-item :complete="minorAccountLinkProcess > 2" value="2">
            {{ $t('mal_step_review_header') }}
          </v-stepper-item>
          <v-divider />
          <v-stepper-item :complete="minorAccountLinkProcess > 3" value="3">
            {{ $t('mal_step_confirmation_header') }}
          </v-stepper-item>
        </v-stepper-header>
        <div v-if="asyncProcessInProgress" class="text-center ma-4">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <v-stepper-window>
          <v-stepper-item
            :complete="minorAccountLinkProcess > 1"
            value="1"
            v-if="isMobileView"
            :class="minorAccountLinkProcess === '1' ? 'my-step-complete' : ''"
          >
            {{ $t('mal_step_enter_information_header') }}
          </v-stepper-item>
          <v-stepper-window-item value="1">
            <!-- has async process -->
            <EnterInformation
              :disable-actions="asyncProcessInProgress"
              @continueClicked="searchForMinorAccounts"
              @cancelClicked="cancelMinorAccountLinkStep()"
            />
          </v-stepper-window-item>

          <v-stepper-item
            :complete="minorAccountLinkProcess > 2"
            value="2"
            v-if="isMobileView"
            :class="minorAccountLinkProcess === '2' ? 'my-step-complete' : ''"
          >
            {{ $t('mal_step_review_header') }}
          </v-stepper-item>
          <v-stepper-window-item value="2">
            <!-- has async process -->
            <InformationReview
              :disable-actions="asyncProcessInProgress"
              :minor-accounts="minorAccountsFound"
              @continueClicked="reviewFoundMinorAccountsToBeLinked"
              @cancelClicked="cancelMinorAccountLinkStep()"
            />
          </v-stepper-window-item>

          <v-stepper-item
            :complete="minorAccountLinkProcess > 3"
            value="3"
            v-if="isMobileView"
            :class="minorAccountLinkProcess === '3' ? 'my-step-complete' : ''"
          >
            {{ $t('mal_step_confirmation_header') }}
          </v-stepper-item>
          <v-stepper-window-item value="3">
            <LinkConfirmation
              :disable-actions="asyncProcessInProgress"
              :minor-accounts-linked="minorAccountsLinked"
              :minor-accounts-not-linked="minorAccountsNotLinked"
              @continueClicked="confirmLinkedMinorAccounts()"
              @cancelClicked="cancelMinorAccountLinkStep()"
            />
          </v-stepper-window-item>
        </v-stepper-window>
      </v-stepper>
    </v-container>
  </v-main>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import EnterInformation from '../components/EnterInformation.vue';
import InformationReview from '../components/InformationReview.vue';
import LinkConfirmation from '../components/LinkConfirmation.vue';
import MessageDialog from '../../../components/MessageDialog.vue';
import { mapActions, mapState } from 'pinia';
import { useCustomerStore } from '@/stores/customer/customer.store';
import NavigationConfirmationDialog from '@/components/NavigationConfirmation/NavigationConfirmationDialog.vue';
import { useReinstatementStore } from '@/modules/Reinstatement/store/reinstatement.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { mdiLink } from '@mdi/js';
import { formatDateObjectToISODate } from '@/shared/utils/dateFormats';
import { RouteNames } from '@/router/routeNames';
import type { ICustodialAccountMeta } from '@/shared/api/models.bff';
import type { RouteParamsGeneric } from 'vue-router';

const componentOptions: ComponentOptions = {
  name: 'MinorAccountLinkLayout',
  components: {
    EnterInformation,
    InformationReview,
    LinkConfirmation,
    NavigationConfirmationDialog,
    MessageDialog,
  },
  data() {
    return {
      mdiLink,
      asyncProcessInProgress: false,
      listener: null as ((this: Window, ev: UIEvent) => unknown) | null,
      minorAccountLinkProcess: '1',
      minorAccountsFound: [] as ICustodialAccountMeta[],
      minorAccountsLinked: [] as ICustodialAccountMeta[],
      minorAccountsNotLinked: [] as ICustodialAccountMeta[],
      mobileView: false,
      showNavigationConfirmationDialog: false,
      navigatingToRoute: '',
      navigatingToParams: {} as RouteParamsGeneric,
      navigationAwayConfirmed: false,
      showMinorAccountNotFound: false,
      showMinorAccountsNotLinked: false,
      customerServicePhoneNumber: '(800) 257-4725',
    };
  },
  computed: {
    ...mapState(usePolicyStore, { currentPolicy: 'currentPolicy' }),
    isMobileView() {
      return this.$vuetify?.display?.xs;
    },
  },
  methods: {
    mdiLink() {
      return mdiLink;
    },
    ...mapActions(useCustomerStore, ['searchMinorAccount', 'linkMinorAccount', 'getInvestments']),
    ...mapActions(useReinstatementStore, ['postPolicyReinstatement']),
    async searchForMinorAccounts({ dob, taxId }: { dob: Date; taxId: string }) {
      this.asyncProcessInProgress = true;
      const minorAcctsFound: ICustodialAccountMeta[] = await this.searchMinorAccount({
        taxId, // '539968784',
        dob: formatDateObjectToISODate(dob), // '1994-01-30',
      })
        .catch(() => {
          this.asyncProcessInProgress = false;
          return [];
        })
        .finally(() => {
          this.asyncProcessInProgress = false;
        });

      this.minorAccountsFound = minorAcctsFound;

      if (!minorAcctsFound.length) {
        this.showMinorAccountNotFound = true;
      }

      // go to the next OR stay on the current minor account link process
      this.minorAccountLinkProcess = minorAcctsFound.length ? '2' : '1';
    },
    async reviewFoundMinorAccountsToBeLinked() {
      this.asyncProcessInProgress = true;
      const linkAccountsPromise = this.minorAccountsFound.map((acct: ICustodialAccountMeta) => {
        const linkMinorAccountPromise = this.linkMinorAccount(acct.accountId)
          .then((data: boolean) => ({ ...{ isLinked: data }, ...acct }))
          .catch(() => Promise.resolve({ ...{ isLinked: false }, ...acct }));
        return linkMinorAccountPromise;
      });

      await Promise.allSettled(linkAccountsPromise)
        .then(
          (results: Array<PromiseSettledResult<ICustodialAccountMeta & { isLinked: boolean }>>) => {
            const successfullyLinkedRequests = results.filter(
              (r) => r.status === 'fulfilled' && r.value.isLinked
            );
            const failedLinkedRequests = results.filter(
              (r) => r.status === 'rejected' || !r.value.isLinked
            );
            const linkedMinorAccountIds = successfullyLinkedRequests.map(
              (r) =>
                (r as PromiseFulfilledResult<ICustodialAccountMeta & { isLinked: boolean }>).value
                  .accountId
            ); // array of successfully linked account ids
            const nonlinkedMinorAccountIds = failedLinkedRequests.map(
              (r) =>
                (r as PromiseFulfilledResult<ICustodialAccountMeta & { isLinked: boolean }>).value
                  .accountId
            ); // array of unsuccessfuly linked account ids
            this.minorAccountsLinked = this.minorAccountsFound.filter(
              (acct: ICustodialAccountMeta) => linkedMinorAccountIds.includes(acct.accountId)
            );
            this.minorAccountsNotLinked = this.minorAccountsFound.filter(
              (acct: ICustodialAccountMeta) => nonlinkedMinorAccountIds.includes(acct.accountId)
            );
          }
        )
        .catch(() => {
          this.asyncProcessInProgress = false;
        })
        .finally(() => {
          this.asyncProcessInProgress = false;
        });

      // no minor accounts linked
      if (!this.minorAccountsLinked.length) {
        this.showMinorAccountsNotLinked = true;
      }
      // go to the next OR stay on the current minor account link process
      this.minorAccountLinkProcess = this.minorAccountsLinked.length ? '3' : '2';
    },
    async confirmLinkedMinorAccounts() {
      this.asyncProcessInProgress = true;
      await this.getInvestments().finally(() => {
        this.asyncProcessInProgress = false;
      });
      await this.$router.push({ name: RouteNames.INVESTMENTS });
    },
    async cancelMinorAccountLinkStep() {
      await this.$router.push({ name: RouteNames.INVESTMENTS });
    },
    navigateToRoute() {
      this.navigationAwayConfirmed = true;
      this.$router.push({
        name: this.navigatingToRoute,
        params: this.navigatingToParams,
      });
    },
  },
  watch: {
    isMobileView(newVal /* , oldVal */) {
      this.mobileView = !newVal;
    },
  },
  created() {
    this.mobileView = this.$vuetify.display.xs;
  },
  beforeUnmount() {
    if (this.listener) {
      window.removeEventListener('resize', this.listener);
    }
  },
  beforeRouteLeave(to, _, next) {
    const isMinorAccountLinkProcessDone =
      this.minorAccountLinkProcess === '3' || this.navigationAwayConfirmed;
    const navigatingOutsideMinorAccountLink =
      to.name !== RouteNames.MINOR_ACCOUNT_LINK_PROCESS &&
      to.path !== '/minor-account-link-process';
    if (navigatingOutsideMinorAccountLink && !isMinorAccountLinkProcessDone) {
      this.showNavigationConfirmationDialog = true; // show NavigationConfirmationDialog
      this.navigatingToRoute = to.name as string; // save the route name
      this.navigatingToParams = to.params;
      next(false); // cancel the navigation
    } else {
      next();
    }
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss">
.containerSize {
  max-width: 1137px;
  margin: auto;
}
.v-theme--primerica.v-divider {
  border-color: #000000 !important;
}
.grey-circle {
  width: 3rem;
  height: 3rem;
  background-color: rgb(217, 217, 217);
  border-radius: 100%;
  padding: 1rem;
}
.bottomSpacing {
  margin-bottom: 200px;
}
</style>
